import React, { useState } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";

const SEGMENT_DATA = [
  { segment: "Entry <$150K", count: 218, pctChange: 1.8, medianDom: 32, absorption: 3.8 },
  { segment: "$150K–$250K", count: 524, pctChange: 4.2, medianDom: 26, absorption: 2.9 },
  { segment: "$250K–$350K", count: 468, pctChange: 5.8, medianDom: 22, absorption: 2.4 },
  { segment: "$350K–$500K", count: 312, pctChange: 6.4, medianDom: 24, absorption: 2.8 },
  { segment: "$500K–$750K", count: 148, pctChange: 4.9, medianDom: 34, absorption: 4.1 },
  { segment: "Luxury >$750K", count: 52, pctChange: 2.6, medianDom: 54, absorption: 6.2 },
];

const VIEWS = [
  { key: "count", label: "Listings", color: "var(--color-primary)" },
  { key: "pctChange", label: "Appreciation %", color: "var(--color-secondary)" },
  { key: "absorption", label: "Absorption (mo)", color: "var(--color-warning)" },
];

const CustomTooltip = ({ active, payload, label, view }) => {
  if (!active || !payload?.length) return null;
  const d = SEGMENT_DATA?.find(s => s?.segment === label);
  return (
    <div className="bg-card border border-border rounded-xl p-3 shadow-lg">
      <p className="font-heading font-semibold text-sm text-foreground mb-2">{label}</p>
      <div className="space-y-1 text-xs font-caption">
        <div className="flex justify-between gap-6">
          <span className="text-muted-foreground">Active Listings</span>
          <span className="font-semibold text-foreground">{d?.count}</span>
        </div>
        <div className="flex justify-between gap-6">
          <span className="text-muted-foreground">YoY Appreciation</span>
          <span className="font-semibold text-emerald-600">+{d?.pctChange}%</span>
        </div>
        <div className="flex justify-between gap-6">
          <span className="text-muted-foreground">Median DOM</span>
          <span className="font-semibold text-foreground">{d?.medianDom} days</span>
        </div>
        <div className="flex justify-between gap-6">
          <span className="text-muted-foreground">Absorption Rate</span>
          <span className="font-semibold text-foreground">{d?.absorption} mo</span>
        </div>
      </div>
    </div>
  );
};

export default function MarketSegmentChart() {
  const [activeView, setActiveView] = useState(VIEWS?.[0]);

  const maxVal = Math.max(...SEGMENT_DATA?.map(d => d?.[activeView?.key]));

  return (
    <div className="card-base flex flex-col gap-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h3 className="font-heading font-semibold text-base text-foreground">Price Segment Distribution</h3>
          <p className="text-xs font-caption text-muted-foreground mt-0.5">Market activity by price range</p>
        </div>
        <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
          {VIEWS?.map(v => (
            <button
              key={v?.key}
              onClick={() => setActiveView(v)}
              className={`px-2.5 py-1 rounded-md text-xs font-caption font-medium transition-standard ${
                activeView?.key === v?.key
                  ? "bg-card text-primary shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {v?.label}
            </button>
          ))}
        </div>
      </div>
      <div className="w-full h-52" aria-label="Market segment distribution bar chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={SEGMENT_DATA} margin={{ top: 16, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
            <XAxis
              dataKey="segment"
              tick={{ fontSize: 10, fontFamily: "Inter", fill: "var(--color-muted-foreground)" }}
              axisLine={false}
              tickLine={false}
              interval={0}
              angle={-20}
              textAnchor="end"
              height={40}
            />
            <YAxis
              tick={{ fontSize: 11, fontFamily: "Inter", fill: "var(--color-muted-foreground)" }}
              axisLine={false}
              tickLine={false}
              width={36}
            />
            <Tooltip content={<CustomTooltip view={activeView?.key} />} />
            <Bar dataKey={activeView?.key} radius={[4, 4, 0, 0]}>
              {SEGMENT_DATA?.map((entry, i) => (
                <Cell
                  key={i}
                  fill={entry?.[activeView?.key] === maxVal ? activeView?.color : "var(--color-muted)"}
                  opacity={entry?.[activeView?.key] === maxVal ? 1 : 0.65}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}